import React from 'react';
import styled from 'styled-components';
import { Grid } from '@wegroup/design-system';
import { Tooltip } from 'wg-fe-ui';

// Components
import SelectionItem from './SelectionItem';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  pushToSelectedCompanies,
  popFromSelectedCompanies,
} from '../../store/redux/slices/selectionSlice';

// Types
import { RootState } from '../../store/redux/reducers/rootReducer';

import { Company, DisabledObj } from '../../../../../types/ProductFactory';
import SupportedRiskObjects from './SupportedRiskObjects';
import SelectAllContainer from './SelectAllContainer';
import { handleResponseLanguage } from '../../../../../i18n';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';

interface Props {
  companies: Company[];
}

const CompanySelection: React.FC<React.PropsWithChildren<Props>> = ({
  companies,
}) => {
  const { selectedRiskObjects, selectedCompanies, riskObjects } = useSelector(
    (state: RootState) => state.selection,
  );
  const dispatch = useDispatch();

  const handleOnSelected = (name: string) => {
    if (selectedCompanies.includes(name))
      return dispatch(popFromSelectedCompanies(name));
    return dispatch(pushToSelectedCompanies(name));
  };

  const isCompanyDisabled = (
    insurances: string[],
    disabledObj?: DisabledObj,
  ) => {
    return (
      disabledObj?.insurance_types?.length === 0 ||
      selectedRiskObjects
        .filter((riskObject) => insurances?.includes(riskObject))
        .every((riskObject) =>
          disabledObj?.insurance_types?.includes(riskObject?.toLowerCase()),
        )
    );
  };

  const isAllSelected = companies
    .filter(
      ({ insurances, disabledObj }) =>
        !isCompanyDisabled(insurances, disabledObj),
    )
    .every(({ name }) => selectedCompanies.includes(name));

  const handleOnSelectAll = () => {
    if (isAllSelected)
      dispatch(popFromSelectedCompanies(selectedCompanies.map((name) => name)));
    else
      dispatch(
        pushToSelectedCompanies(
          companies
            .filter(
              ({ name, insurances, disabledObj }) =>
                !selectedCompanies.includes(name) &&
                !isCompanyDisabled(insurances, disabledObj),
            )
            .map(({ name }) => name),
        ),
      );
  };

  return (
    <Grid
      templateColumns={{
        base: '100%',
        tabletS: 'repeat(2, 1fr)',
        tablet: 'repeat(auto-fill, 24rem)',
      }}
      gridGap="6"
      justifyContent="center"
      data-test-id="ProdFac_CompanySelection"
    >
      <StyledSelectAllContainer
        onClick={handleOnSelectAll}
        allSelected={isAllSelected}
      />
      {companies.map(({ name, logo, disabledObj, insurances }) => (
        <StyledSelectionItem
          key={`selection_item_${name}`}
          type={name}
          imgSrc={logo}
          selected={selectedCompanies.includes(name)}
          onSelected={() => handleOnSelected(name)}
          disabled={isCompanyDisabled(insurances, disabledObj)}
          disableMessage={handleResponseLanguage(disabledObj?.reason)}
          disableSeverity={disabledObj?.severity}
          BottomComp={
            <>
              <SupportedRiskObjects
                riskObjects={riskObjects}
                supportedRiskObject={insurances}
              />
              {!!disabledObj &&
                disabledObj?.reason &&
                !isCompanyDisabled(insurances, disabledObj) && (
                  <StyledTooltip direction="left" toggleIcon="!">
                    {handleResponseLanguage(
                      handleResponseLanguage(disabledObj?.reason),
                    )}
                  </StyledTooltip>
                )}
            </>
          }
          smallImage
          data-test-id={`ADVISORY-CONVO_SELECTION-VIEW_SELECTION-ITEM_COMPANY-${name}`}
        />
      ))}
    </Grid>
  );
};

const StyledTooltip = styled(Tooltip)`
  z-index: 11;
  position: absolute;
  top: 1.6rem;
  right: 1.6rem;

  text-align: left;
  > div {
    opacity: 1;
    width: 25rem;
    z-index: 10;
    font-style: normal;
    overflow: visible;
    left: -515%;
  }
`;

const StyledSelectAllContainer = styled(SelectAllContainer)`
  grid-column: 1 / -1;
  padding-bottom: 0;
`;

const StyledSelectionItem = styled(SelectionItem)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${mq.mobileM} {
    flex-direction: row;
    grid-template-rows: 1fr 2fr;
    > div:first-of-type {
      border-right: 1px solid #f0f1f3;
      > div {
        padding: 0;
        height: 100%;
      }
    }
    > div:last-of-type p {
      border-top: none;
    }
  }

  ${mq.tabletS} {
    flex-direction: column;
  }
`;

export default CompanySelection;
